import { matchPath } from "react-router"

/**
 * Legacy URL compatibility for the Vite + React Router SPA (NEX-248 Phase C).
 *
 * Next served these as server-side `rewrites`/`redirects` in `next.config.mjs`.
 * A static SPA has no server, so the same table is resolved client-side: on every
 * navigation {@link resolveLegacyRedirect} maps an old URL to its canonical
 * destination, and the app shell issues a `<Navigate replace>`.
 *
 * Two source shapes are folded in here:
 *  - PascalCase legacy paths (`/App/MyAgents` → `/app/my-agents`). Pure-casing
 *    variants (`/App/Chats`) are matched case-sensitively so they never shadow
 *    the real lowercase route; structural renames (`MyAgents` → `my-agents`,
 *    `Integrations` → `deployments`) genuinely need the entry.
 *  - In-app moves (`/app/integrations/*` → `/app/deployments`, `/app/inbox2` →
 *    `/app/inbox`), some carrying a destination query string.
 *
 * Host-level rules are intentionally NOT here — they must act before the SPA
 * loads and so live in the static-host config: `/` → marketing 301, and the
 * `.well-known/llms*` / `/docs/llms*` / `/sitemap.xml` / `/api/docs/*` proxies
 * to the backend.
 *
 * `:param` tokens in a destination are filled from the matched path params;
 * a trailing catch-all token (`:params*`/`:index*`/`:path*`) is filled from the
 * splat remainder. Patterns are matched with `end: true` (full-path) and
 * `caseSensitive: true`.
 */

interface LegacyRedirect {
  /** React Router path pattern matched against the incoming pathname. */
  pattern: string
  /** Destination template; `:param` / trailing `:name*` are substituted. */
  to: string
}

// Only these prefixes can ever match — checked first so normal navigation skips
// the table entirely. Case-insensitive to catch `/App` and `/app/...` alike.
const LEGACY_PREFIX = /^\/(app|embed)\b/i

const LEGACY_REDIRECTS: LegacyRedirect[] = [
  { pattern: "/App", to: "/app" },
  { pattern: "/App/BugReport", to: "/app/bug-report" },
  { pattern: "/App/Chats", to: "/app/chats" },
  { pattern: "/App/Chats/New", to: "/app/chats/new" },
  { pattern: "/App/Chats/:chatId", to: "/app/chats/:chatId" },
  { pattern: "/App/CreateOrganization/*", to: "/app/create-organization/:params*" },
  { pattern: "/App/Dashboard", to: "/app/dashboard" },
  { pattern: "/App/DocumentTemplates", to: "/app/document-templates" },
  { pattern: "/App/DocumentTemplates/:id", to: "/app/document-templates/:id" },
  { pattern: "/App/DocumentTemplates/ExcelTemplate", to: "/app/document-templates/excel-template" },
  {
    pattern: "/App/DocumentTemplates/PowerPointTemplate",
    to: "/app/document-templates/power-point-template"
  },
  {
    pattern: "/App/DocumentTemplates/PowerPointTemplateSplit",
    to: "/app/document-templates/power-point-template-split"
  },
  { pattern: "/App/DocumentTemplates/WordTemplate", to: "/app/document-templates/word-template" },
  { pattern: "/App/Documents", to: "/app/documents" },
  { pattern: "/App/Documents/Folder/:id", to: "/app/documents/folder/:id" },
  { pattern: "/App/Documents/google-callback", to: "/app/documents/google-callback" },
  { pattern: "/App/Documents/GoogleCallback", to: "/app/documents/google-callback" },
  { pattern: "/App/Documents/notion-callback", to: "/app/documents/notion-callback" },
  { pattern: "/App/Documents/NotionCallback", to: "/app/documents/notion-callback" },
  { pattern: "/App/Documents/sharepoint-callback", to: "/app/documents/sharepoint-callback" },
  { pattern: "/App/Documents/SharepointCallback", to: "/app/documents/sharepoint-callback" },
  { pattern: "/App/Inbox", to: "/app/inbox" },
  { pattern: "/App/Integrations", to: "/app/deployments" },
  {
    pattern: "/App/Integrations/AddAirtableIntegration",
    to: "/app/deployments/add-airtable-deployment"
  },
  { pattern: "/App/Integrations/AddApiIntegration", to: "/app/deployments/add-api-deployment" },
  { pattern: "/App/Integrations/AddEmbedIntegration", to: "/app/deployments/add-embed-deployment" },
  { pattern: "/App/Integrations/AddFacebookLogin", to: "/app/deployments/add-facebook-login" },
  {
    pattern: "/App/Integrations/AddGoogleSheetIntegration",
    to: "/app/deployments/add-google-sheet-deployment"
  },
  {
    pattern: "/App/Integrations/AddPhoneCallIntegration",
    to: "/app/deployments/add-phone-call-deployment"
  },
  { pattern: "/App/Integrations/AddSlackIntegration", to: "/app/deployments/add-slack-deployment" },
  { pattern: "/App/Integrations/AddTeamsIntegration", to: "/app/deployments/add-teams-deployment" },
  {
    pattern: "/App/Integrations/AddTelegramIntegration",
    to: "/app/deployments/add-telegram-deployment"
  },
  {
    pattern: "/App/Integrations/AddTwilioIntegration",
    to: "/app/deployments/add-twilio-deployment"
  },
  {
    pattern: "/App/Integrations/AddWhatsAppIntegration",
    to: "/app/deployments/add-whatsapp-deployment"
  },
  {
    pattern: "/App/Integrations/AddWhatsappIntegration",
    to: "/app/deployments/add-whatsapp-deployment"
  },
  { pattern: "/App/Integrations/Excel", to: "/app/deployments/excel" },
  { pattern: "/App/Integrations/GoogleSheet", to: "/app/deployments/google-sheet" },
  { pattern: "/App/Integrations/OutlookAddIn", to: "/app/deployments/outlook-add-in" },
  { pattern: "/App/Integrations/PowerPoint", to: "/app/deployments/power-point" },
  { pattern: "/App/Integrations/TwilioIntegration", to: "/app/deployments/twilio-integration" },
  { pattern: "/App/Integrations/Word", to: "/app/deployments/word" },
  { pattern: "/App/Invitations", to: "/app/invitations" },
  { pattern: "/App/Marketplace/Tasks/:taskId/Tabs/:tabKey", to: "/app/my-ai-tasks/:taskId" },
  { pattern: "/App/MyAgents", to: "/app/my-agents" },
  { pattern: "/App/MyAgents/:agentId/Tabs/:tabKey", to: "/app/my-agents/:agentId/tabs/:tabKey" },
  // THE RULE FOR A DESTINATION WHOSE PAGE IS GONE: re-point to the parent only
  // when the parent surface HOSTS the affordance the old URL named. Otherwise
  // drop the entry — a redirect onto a page that cannot do what the URL asked
  // for is a soft 404, and it reads as having arrived, which is worse than the
  // real one. This is the only entry below that earns the re-point; the model
  // sub-surfaces further down are the same situation failing the same test.
  //
  // Agent creation lives at `/app/agent-creation/new`: a registered wizard
  // (`routes.tsx` → `app/agent-creation/*`, backed by `modules/AgentCreation`),
  // and the surface that actually performs it. `/app/my-agents` only renders a
  // button that navigates there, so the list page does NOT host the affordance
  // and pointing this entry at it is the soft 404 the rule above refuses.
  //
  // `MyAgents/CreateAgentButton` holds a SECOND path and it is not the default:
  // `handleFastCreate` POSTs and jumps to the new agent's prompt tab, but it
  // renders only while Alt is held, as a developer shortcut. The ordinary press
  // is `handleCreateAgentClick` → `/app/agent-creation/new`. Read the render
  // branches before treating any navigation in that file as the creation path.
  { pattern: "/App/MyAgents/CreateAgent", to: "/app/agent-creation/new" },
  { pattern: "/App/MyTools", to: "/app/my-tools" },
  // THE REGISTERED TOOL OAUTH redirect_uri, AND IT IS LIVE TRAFFIC, NOT HISTORY.
  // `StartExternalToolOAuthFlowUseCase` (backend `tools/external-tool-oauth/`)
  // builds `${FRONTEND_BASE_URL}/App/MyTools/oauth/callback/${toolId}` in its
  // `externalToolOAuthRedirectUri` helper, and tenants registered that exact
  // string with their providers, so it cannot change. React Router's own
  // case-insensitive matching does not rescue it: `MyTools` against `my-tools`
  // is a structural rename, not a casing variant, so without this entry every
  // provider sign-in lands on the 404 page.
  { pattern: "/App/MyTools/oauth/callback/:toolId", to: "/app/my-tools/oauth/callback/:toolId" },
  { pattern: "/App/MyTools/Oauth/Callback/:toolId", to: "/app/my-tools/oauth/callback/:toolId" },
  {
    pattern: "/App/MyTools/Oauth2Credentials/Callback/:toolId",
    to: "/app/my-tools/oauth2-credentials/callback/:toolId"
  },
  // `/app/settings/models` is a read-only catalogue and the only surviving
  // model route. Its Next-era children are deliberately absent: per-model
  // detail was never rebuilt, custom-model authoring moved into a modal inside
  // `common/components/ModelPicker` that no URL reaches, and model hosting does
  // not exist anywhere in the product. Redirecting those three here would land
  // a browser on a page that cannot do what the URL named, so they are dropped
  // rather than re-pointed.
  { pattern: "/App/Settings/Models", to: "/app/settings/models" },
  { pattern: "/App/Settings/OrganizationList/*", to: "/app/settings/organization-list/:index*" },
  { pattern: "/App/Settings/Profile/*", to: "/app/settings/profile/:index*" },
  { pattern: "/App/Settings", to: "/app/settings" },
  { pattern: "/App/Settings/General", to: "/app/settings/general" },
  { pattern: "/App/Settings/Developer", to: "/app/settings/developer" },
  { pattern: "/App/Settings/UserGroups", to: "/app/settings/user-groups" },
  { pattern: "/App/Settings/PhoneNumber", to: "/app/settings/phone-number" },
  { pattern: "/App/Settings/Macros", to: "/app/settings/macros" },
  { pattern: "/App/Settings/Billing", to: "/app/settings/billing" },
  { pattern: "/App/Onboarding", to: "/app/onboarding" },
  { pattern: "/App/TestChat", to: "/app/test-chat" },
  { pattern: "/App/Triggers", to: "/app/triggers" },
  { pattern: "/App/UserProfile", to: "/app/user-profile" },
  { pattern: "/App/Workflows", to: "/app/workflows" },
  { pattern: "/App/Workflows/:id", to: "/app/workflows/:id" },
  { pattern: "/Embed/:embedId", to: "/embed/:embedId" },
  { pattern: "/app/inbox2", to: "/app/inbox" },
  { pattern: "/app/inbox2/*", to: "/app/inbox/:path*" },
  { pattern: "/app/integrations", to: "/app/deployments" },
  {
    pattern: "/app/integrations/add-slack-integration",
    to: "/app/deployments?action=new-deployment&type=SLACK"
  },
  {
    pattern: "/app/integrations/add-teams-integration",
    to: "/app/deployments?action=new-deployment&type=TEAMS"
  },
  {
    pattern: "/app/integrations/add-twilio-integration",
    to: "/app/deployments?action=new-deployment&type=TWILIO_SMS"
  },
  {
    pattern: "/app/integrations/add-whatsapp-integration",
    to: "/app/deployments?action=new-deployment&type=WHATSAPP"
  },
  {
    pattern: "/app/integrations/add-api-integration",
    to: "/app/deployments?action=new-deployment&type=API"
  },
  {
    pattern: "/app/integrations/add-embed-integration",
    to: "/app/deployments?action=new-deployment&type=EMBED"
  },
  {
    pattern: "/app/integrations/add-phone-call-integration",
    to: "/app/deployments?action=new-deployment&type=PHONE_CALL"
  },
  {
    pattern: "/app/integrations/add-airtable-integration",
    to: "/app/deployments?action=new-deployment&type=AIRTABLE"
  },
  {
    pattern: "/app/integrations/add-google-sheet-integration",
    to: "/app/deployments?action=new-deployment&type=GOOGLE_SHEETS"
  },
  {
    pattern: "/app/integrations/add-google-meet-integration",
    to: "/app/deployments?action=new-deployment&type=GOOGLE_MEET"
  },
  {
    pattern: "/app/integrations/add-zoom-integration",
    to: "/app/deployments?action=new-deployment&type=ZOOM"
  },
  { pattern: "/app/integrations/add-facebook-login", to: "/app/deployments/add-facebook-login" },
  { pattern: "/app/integrations/excel", to: "/app/deployments/excel" },
  { pattern: "/app/integrations/google-sheet", to: "/app/deployments/google-sheet" },
  { pattern: "/app/integrations/outlook-add-in", to: "/app/deployments/outlook-add-in" },
  { pattern: "/app/integrations/power-point", to: "/app/deployments/power-point" },
  { pattern: "/app/integrations/word", to: "/app/deployments/word" },
  { pattern: "/app/integrations/twilio-integration", to: "/app/deployments/twilio-integration" }
]

/**
 * Every destination this table can send a browser to, as authored.
 *
 * A redirect is a link like any other and has to land on a registered route,
 * but nothing here can check that — `routes.tsx` is the only thing that knows
 * what is registered, and importing it from this module would pull the whole
 * route table into the navigation path. So the destinations are published and
 * `route-reachability.test.ts` sweeps them against the real table there.
 *
 * Derived from `LEGACY_REDIRECTS` rather than restated, so the two cannot drift.
 */
export const LEGACY_REDIRECT_DESTINATIONS: readonly string[] = LEGACY_REDIRECTS.map(({ to }) => to)

/** Substitute matched params into a destination template. */
function applyParams(to: string, params: Readonly<Record<string, string | undefined>>): string {
  const splat = params["*"] ?? ""
  // Catch-all token first (`:params*`/`:index*`/`:path*` → splat remainder).
  let result = to.replace(/:[A-Za-z0-9_]+\*/g, splat)
  for (const [key, value] of Object.entries(params)) {
    if (key === "*" || value === undefined) continue
    result = result.replace(`:${key}`, value)
  }
  // An empty catch-all leaves a dangling slash (`/app/inbox/` → `/app/inbox`).
  // Guard the query string so a trailing `?...` is preserved.
  const queryIndex = result.indexOf("?")
  const path = queryIndex === -1 ? result : result.slice(0, queryIndex)
  const query = queryIndex === -1 ? "" : result.slice(queryIndex)
  const trimmed = path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path
  return `${trimmed}${query}`
}

/**
 * Carry the incoming query string and hash onto the destination. Next's
 * server-side rewrites preserved the full URL while serving the canonical route;
 * the client-side redirect must do the same, or `/App/Chats?tab=x#y` would lose
 * `?tab=x#y`. A destination that defines its own params (the integration
 * redirects' `?action=…&type=…`) wins on key conflict — that mapping is the
 * redirect's explicit intent.
 *
 * A destination with no query of its own takes the incoming query VERBATIM.
 * Re-serialising it through `URLSearchParams` is not a no-op: it rewrites
 * `%20` to `+` and `/` to `%2F`, so an OAuth provider's `code` would reach the
 * callback as different bytes than the provider sent. Only a real merge pays
 * that cost, because only a merge needs a parsed form.
 */
function mergeIncoming(target: string, search: string, hash: string): string {
  const queryIndex = target.indexOf("?")
  const path = queryIndex === -1 ? target : target.slice(0, queryIndex)
  const targetQuery = queryIndex === -1 ? "" : target.slice(queryIndex + 1)

  const incoming = search.startsWith("?") ? search.slice(1) : search
  if (targetQuery === "") return `${path}${incoming ? `?${incoming}` : ""}${hash}`

  const params = new URLSearchParams(incoming)
  for (const [key, value] of new URLSearchParams(targetQuery)) params.set(key, value)

  const qs = params.toString()
  return `${path}${qs ? `?${qs}` : ""}${hash}`
}

/**
 * Resolve a legacy URL to its canonical destination (with the incoming query +
 * hash carried over), or `null` if it is not a legacy route (the overwhelmingly
 * common case). Pure — safe to call during render. `search`/`hash` come straight
 * from `useLocation()` (each includes its `?`/`#` prefix, or is empty).
 */
export function resolveLegacyRedirect(pathname: string, search = "", hash = ""): string | null {
  if (!LEGACY_PREFIX.test(pathname)) return null
  for (const { pattern, to } of LEGACY_REDIRECTS) {
    const match = matchPath({ path: pattern, caseSensitive: true, end: true }, pathname)
    if (match) return mergeIncoming(applyParams(to, match.params), search, hash)
  }
  return null
}
